import { useEffect, useState } from "react";
import { api, type SmsStats, type User } from "../api";
import { goBack, pushScreen } from "../backstack";
import BackHeader from "../components/BackHeader";
import PinDialog from "../components/PinDialog";
import UsersScreen from "../scheduler/UsersScreen";

const LABEL = "px-1 text-[12.5px] font-semibold text-[#3d2930]";

/** Same shaping the server does before sending: "0047…", "412 61 013" → E.164. */
function normalizePhone(raw: string): string | null {
  let s = raw.replace(/[\s\-().]/g, "");
  if (s.startsWith("00")) s = `+${s.slice(2)}`;
  if (/^\d{8}$/.test(s)) s = `+47${s}`;
  return /^\+[1-9]\d{7,14}$/.test(s) ? s : null;
}

/** "2026-09-08" → "8 Sept" (weeks start on Monday). */
function weekLabel(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** Hidden admin page (/admin): SMS volume per week and remaining Twilio credit. */
export default function AdminScreen({ onBack }: { onBack: () => void }) {
  const [stats, setStats] = useState<SmsStats | null>(null);
  const [pin, setPin] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [showUsers, setShowUsers] = useState(false);
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [loggedNumbers, setLoggedNumbers] = useState<string[]>([]);
  const [checked, setChecked] = useState<Set<string> | null>(null);

  useEffect(() => {
    api.users().then(setUsers).catch(() => {});
    // Numbers the system has already texted (for ad-hoc recipients beyond the users).
    api
      .smsHistory()
      .then((rows) => setLoggedNumbers([...new Set(rows.map((r) => r.to))]))
      .catch(() => {});
  }, []);

  // Recipient list: active users with a number (pre-checked), then any other
  // number the system has sent SMS to before (unchecked until ticked).
  const userRecipients = users
    .filter((u) => u.active && u.phone)
    .map((u) => ({ label: u.name, number: normalizePhone(u.phone!) }))
    .filter((r): r is { label: string; number: string } => r.number !== null)
    .sort((a, b) => a.label.localeCompare(b.label));
  const userNumbers = new Set(userRecipients.map((r) => r.number));
  const recipientRows = [
    ...userRecipients,
    ...loggedNumbers.filter((n) => !userNumbers.has(n)).map((n) => ({ label: n, number: n })),
  ];

  // Default selection once the users have loaded: everyone with a number.
  useEffect(() => {
    if (checked === null && userRecipients.length > 0) {
      setChecked(new Set(userRecipients.map((r) => r.number)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const toggleNumber = (number: string) => {
    setChecked((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(number)) next.delete(number);
      else next.add(number);
      return next;
    });
  };

  const selectedRows = recipientRows.filter((r) => checked?.has(r.number));

  const openUsers = () => {
    setShowUsers(true);
    pushScreen(() => setShowUsers(false));
  };

  if (showUsers) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 pb-6 pt-4">
        <UsersScreen
          users={users}
          onChanged={(u) =>
            setUsers((list) =>
              list.some((x) => x.id === u.id) ? list.map((x) => (x.id === u.id ? u : x)) : [...list, u],
            )
          }
          onBack={goBack}
        />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col gap-5 px-4 pb-6 pt-4">
        <BackHeader title="Admin" onBack={onBack} />
        <PinDialog
          title="Admin"
          detail="Enter the PIN to see SMS totals and the account balance."
          confirmLabel="Continue"
          confirmBusyLabel="Loading…"
          confirmClass="bg-rose-grad text-white shadow-button"
          onConfirm={async (entered) => {
            setStats(await api.adminSmsStats(entered));
            setPin(entered);
          }}
          onCancel={onBack}
        />
      </div>
    );
  }

  const sendToAll = async () => {
    const text = message.trim();
    if (!text || sending || selectedRows.length === 0) return;
    setConfirming(false);
    setSending(true);
    setSendResult(null);
    try {
      const r = await api.adminBroadcastSms(text, pin, selectedRows.map((s) => s.number));
      const failures = r.results.filter((x) => !x.ok);
      setSendResult(
        `Sent to ${r.sent} ${r.sent === 1 ? "recipient" : "recipients"}` +
          (r.failed ? ` — failed for ${failures.map((f) => f.name).join(", ")}` : "") +
          ".",
      );
      if (r.failed === 0) setMessage("");
    } catch (err) {
      setSendResult(err instanceof Error ? err.message : "Could not send");
    } finally {
      setSending(false);
    }
  };

  const totalSent = stats.weeks.reduce((sum, w) => sum + w.sent, 0);
  const totalCost = stats.weeks.reduce((sum, w) => sum + w.costNok, 0);
  const anyEstimated = stats.weeks.some((w) => w.costEstimated);
  const maxSent = Math.max(1, ...stats.weeks.map((w) => w.sent));

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col gap-5 px-4 pb-6 pt-4">
      <BackHeader
        title="Admin"
        onBack={onBack}
        action={
          <button
            onClick={openUsers}
            className="flex h-10 shrink-0 items-center gap-2 rounded-[12px] border border-[#efdce0] bg-white px-3.5 text-sm font-bold text-[#3a1c25] transition-transform active:scale-95"
          >
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
            Users
          </button>
        }
      />

      <div className="pop-in flex flex-col gap-5">
        <section className="flex flex-col gap-1.5">
          <span className={LABEL}>Money left (Twilio)</span>
          <div className="rounded-[18px] bg-white p-5 border border-line-soft shadow-card">
            {stats.balance ? (
              <>
                <div className="text-[28px] font-bold tracking-[-0.02em]">
                  {stats.balance.nok !== null
                    ? `${stats.balance.nok.toFixed(0)} NOK`
                    : `${stats.balance.amount.toFixed(2)} ${stats.balance.currency}`}
                </div>
                {stats.balance.nok !== null && (
                  <div className="pt-1 text-sm font-semibold text-ink/50">
                    {stats.balance.amount.toFixed(2)} {stats.balance.currency} on the account
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm font-semibold text-ink/50">
                Couldn't read the balance from Twilio right now.
              </p>
            )}
          </div>
        </section>

        <section className="flex flex-col gap-1.5">
          <span className={LABEL}>Message all users</span>
          <div className="flex flex-col gap-3 rounded-[18px] bg-white p-5 border border-line-soft shadow-card">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 320))}
              rows={3}
              placeholder='e.g. "New version of Soulcake App 0.2.0 - https://app.soulcake.eu"'
              className="w-full resize-none rounded-[14px] bg-white p-4 font-semibold outline-none placeholder:text-ink/40 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20"
            />
            <div className="flex flex-col">
              {recipientRows.length === 0 && (
                <p className="text-sm font-semibold text-ink/50">No users with a phone number yet.</p>
              )}
              {recipientRows.map((r) => (
                <label
                  key={r.number}
                  className="flex min-h-11 cursor-pointer items-center gap-3 border-b border-line-soft py-1.5 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={checked?.has(r.number) ?? false}
                    onChange={() => toggleNumber(r.number)}
                    className="size-5 shrink-0 accent-[#d8647a]"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-bold">{r.label}</span>
                  {r.label !== r.number && (
                    <span className="shrink-0 text-xs font-semibold text-ink/50">{r.number}</span>
                  )}
                </label>
              ))}
            </div>
            {sendResult && <p className="text-sm font-semibold">{sendResult}</p>}
            <button
              onClick={() => setConfirming(true)}
              disabled={sending || !message.trim() || selectedRows.length === 0}
              className="h-14 w-full rounded-[14px] bg-rose-grad font-bold text-white transition-transform active:scale-[0.98] disabled:opacity-60 shadow-button"
            >
              {sending
                ? "Sending…"
                : selectedRows.length === recipientRows.length && recipientRows.length > 0
                  ? "Send SMS to all Users"
                  : `Send SMS to ${selectedRows.length} ${selectedRows.length === 1 ? "recipient" : "recipients"}`}
            </button>
          </div>
        </section>

        <section className="flex flex-col gap-1.5">
          <span className={LABEL}>SMS per week</span>
          <div className="flex flex-col gap-2 rounded-[18px] bg-white p-5 border border-line-soft shadow-card">
            {stats.weeks.length === 0 && (
              <p className="text-sm font-semibold text-ink/50">No SMS sent yet.</p>
            )}
            {stats.weeks.map((w) => (
              <div key={w.weekStart} className="flex items-center gap-3">
                <span className="w-16 shrink-0 text-sm font-bold">{weekLabel(w.weekStart)}</span>
                <div className="h-5 min-w-0 flex-1 overflow-hidden rounded-full bg-track">
                  <div
                    className="h-full rounded-full bg-rose"
                    style={{ width: `${(w.sent / maxSent) * 100}%` }}
                  />
                </div>
                <span className="w-8 shrink-0 text-right text-sm font-bold">{w.sent}</span>
                <span className="w-20 shrink-0 text-right text-xs font-semibold text-ink/50">
                  {w.costEstimated ? "~" : ""}
                  {w.costNok.toFixed(2)} kr
                  {w.failed > 0 && <span className="text-danger"> ·{w.failed}✕</span>}
                </span>
              </div>
            ))}
            {stats.weeks.length > 0 && (
              <p className="border-t border-line-soft pt-2 text-xs font-semibold text-ink/50">
                {totalSent} sent · {anyEstimated ? "~" : ""}
                {totalCost.toFixed(2)} NOK total
                {anyEstimated && " (~ = estimated until Twilio reports the price)"}
              </p>
            )}
          </div>
        </section>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-6" onClick={() => setConfirming(false)}>
          <div
            className="pop-in flex w-full max-w-xs flex-col gap-4 rounded-[20px] bg-white p-6 shadow-pop"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-[19px] font-bold tracking-[-0.02em]">
              Send to {selectedRows.length} {selectedRows.length === 1 ? "recipient" : "recipients"}?
            </h2>
            <p className="text-sm font-semibold text-ink/60">
              {selectedRows.map((s) => s.label).join(", ")}
            </p>
            <p className="text-sm font-semibold text-ink/60">"{message.trim()}"</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirming(false)} className="h-12 flex-1 rounded-[14px] bg-white font-bold border border-line text-[#3a1c25]">
                Cancel
              </button>
              <button onClick={sendToAll} className="h-12 flex-1 rounded-[14px] bg-rose-grad font-bold text-white shadow-button">
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
